import { KiloVoltAmps } from "@/libs/units/apparentPower";
import { TAP_SETTING_LOOKUP_ITERATIONS } from "./constants";
const enum TapConfigEnums {
  WYE = "Wye",
  DELTA = "Delta",
  NA = "N/A"
}

export function getSurfaceDriveRequiredKVA(
  requiredVoltage: number,
  motorCurrentA: number
): number {
  return KiloVoltAmps.toThisUnit(requiredVoltage * motorCurrentA * 1.732);
}

export function getSurfaceDriveSystemMonthlyCost(
  surfaceDrivePowerRequiredKW: number,
  powerCostUSD: number
): number {
  return (surfaceDrivePowerRequiredKW * 24 * 365 * powerCostUSD) / 12;
}

// Motor Startup Voltage schtuff.xlsx
export function getMotorStartupVoltage(
  totalImpedance: number,
  reratedNameplateV: number,
  reratedNameplateA: number
): number {
  // =(C25-4*C26*C24)/C25
  // Formula: (reratedNameplateV - 4 * reratedNameplateA * totalImpedance) / reratedNameplateV
  const voltageLoss = 4 * reratedNameplateA * totalImpedance;
  const startupVoltage = reratedNameplateV - voltageLoss;

  return startupVoltage;
}

export type TransformerTapData = {
  tapConfig: TapConfigEnums;
  tapSetting: number;
  availableVoltage: number;
  deltaTapSetting: number;
  wyeTapSetting: number;
  minWye: number;
  maxWye: number;
  minDeltaV: number;
  maxDeltaV: number;
  minSecondaryVolts: number;
  maxSecondaryVolts: number;
};

export function getTransformerTapData(
  minSecondaryVolts: number,
  maxSecondaryVolts: number,
  totalTapVoltage: number
): TransformerTapData {
  const transformerTapData = {
    tapConfig: TapConfigEnums.NA,
    tapSetting: 0,
    availableVoltage: totalTapVoltage,
    deltaTapSetting: 0,
    wyeTapSetting: 0,
    minWye: 0,
    maxDeltaV: 0,
    minSecondaryVolts,
    maxSecondaryVolts
  } as TransformerTapData;

  // If you don't have both, then it's not a valid transformer
  // and you can't calculate the tap settings
  if (minSecondaryVolts === 0 || maxSecondaryVolts === 0) {
    return transformerTapData;
  }
  // l4 - transformer_tap_calcs.1.xlsx
  transformerTapData.minWye = maxSecondaryVolts / 2;

  // i4 - transformer_tap_calcs.1.xlsx
  transformerTapData.maxDeltaV = minSecondaryVolts * 2;

  const transformerVoltLookup = (start: number, totalTapVoltage: number) => {
    let voltLookup = 0;
    const increment = start / TAP_SETTING_LOOKUP_ITERATIONS;
    const limit = Math.round(start + increment * TAP_SETTING_LOOKUP_ITERATIONS);
    let step = start;
    while (step <= limit) {
      if (totalTapVoltage < step && totalTapVoltage > step - increment) {
        voltLookup = step;
        break;
      }
      step = Math.round(step + increment);
    }
    return voltLookup;
  };

  // d12 - transformer_tap_calcs.1.xlsx
  transformerTapData.deltaTapSetting = transformerVoltLookup(
    minSecondaryVolts,
    totalTapVoltage
  );

  // d13 - transformer_tap_calcs.1.xlsx
  transformerTapData.wyeTapSetting = transformerVoltLookup(
    transformerTapData.minWye,
    totalTapVoltage
  );

  // d11 - transformer_tap_calcs.1.xlsx
  transformerTapData.tapConfig =
    totalTapVoltage > transformerTapData.minWye &&
    totalTapVoltage < maxSecondaryVolts
      ? TapConfigEnums.WYE
      : totalTapVoltage > minSecondaryVolts &&
          totalTapVoltage < transformerTapData.maxDeltaV
        ? TapConfigEnums.DELTA
        : TapConfigEnums.NA;

  transformerTapData.tapSetting =
    transformerTapData.tapConfig === TapConfigEnums.WYE
      ? transformerTapData.wyeTapSetting
      : transformerTapData.tapConfig === TapConfigEnums.DELTA
        ? transformerTapData.deltaTapSetting
        : 0;

  transformerTapData.availableVoltage =
    !minSecondaryVolts && !maxSecondaryVolts
      ? totalTapVoltage
      : transformerTapData.tapSetting || totalTapVoltage;

  return transformerTapData;
}
